<template>
  <AppView title="會員登入" :footerShow="true">
    <main
      class="min-h-app bg-gray-50 pb-24 desk:flex desk:min-h-0 desk:flex-1 desk:flex-col desk:bg-white desk:pb-0"
    >
      <div class="desk:grid desk:min-h-0 desk:flex-1 desk:grid-cols-2 desk:items-stretch">
        <section
          class="border-b border-gray-100 bg-white px-5 py-6 desk:flex desk:flex-col desk:justify-start desk:border-b-0 desk:border-r desk:px-8 desk:py-8"
        >
          <p class="text-xs font-medium text-gray-400 desk:text-dove-gray">會員中心</p>
          <h1 class="mt-2 text-xl font-bold text-gray-900 desk:text-2xl">登入達人商城</h1>
          <p class="mt-2 text-sm leading-6 text-gray-500 desk:mt-3">
            使用電話號碼登入後，可查看訂單、管理遊戲 ID 與帳號綁定。
          </p>
          <ul class="mt-8 hidden space-y-7 desk:block">
            <li class="flex items-start gap-3">
              <ClipboardListIcon class="mt-0.5 h-6 w-6 flex-none text-primary" />
              <div>
                <p class="text-sm font-semibold text-gray-900">查看訂單</p>
                <p class="mt-0.5 text-xs leading-5 text-gray-400">登入後可查閱購買紀錄</p>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <IdentificationIcon class="mt-0.5 h-6 w-6 flex-none text-primary" />
              <div>
                <p class="text-sm font-semibold text-gray-900">管理遊戲 ID</p>
                <p class="mt-0.5 text-xs leading-5 text-gray-400">保存常用遊戲帳號</p>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <LinkIcon class="mt-0.5 h-6 w-6 flex-none text-primary" />
              <div>
                <p class="text-sm font-semibold text-gray-900">帳號綁定</p>
                <p class="mt-0.5 text-xs leading-5 text-gray-400">管理第三方登入</p>
              </div>
            </li>
          </ul>
        </section>

        <div
          data-desktop-auth-column
          class="desk:flex desk:flex-col desk:justify-start desk:px-8 desk:py-8"
        >
          <div class="desk:mx-auto desk:w-full desk:max-w-[400px]">
            <section
              v-if="loginMethods.length"
              class="mt-2 border-y border-gray-100 bg-white px-5 py-5 desk:mt-0 desk:border-0 desk:border-b desk:border-gray-100 desk:px-0 desk:pt-0"
            >
              <button
                v-for="method in loginMethods"
                :key="method.name"
                type="button"
                class="mb-3 flex h-12 w-full items-center justify-center rounded-lg text-sm font-bold text-white last:mb-0 hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                :class="method.class"
                :disabled="posting"
                @click.prevent="method.onClick"
              >
                <component :is="method.icon" class="mr-2 inline-block" />
                {{ method.text }}
              </button>
              <p class="mt-3 text-center text-xs leading-5 text-gray-400">
                社交登入後須綁定電話完成註冊；已有電話帳號可綁定合併。
              </p>
              <p class="mt-3 text-center text-sm font-bold text-primary">
                <nuxt-link
                  :to="localePath('/auth/help')"
                  class="desk:hover:underline"
                >無法登錄？查看解決方法</nuxt-link>
              </p>
            </section>

            <section
              class="mt-2 border-y border-gray-100 bg-white px-5 py-5 desk:mt-0 desk:border-0 desk:px-0 desk:py-0"
            >
              <div class="mb-5">
                <h2 class="text-sm font-bold text-gray-900">電話號碼登入</h2>
                <p class="mt-1 text-xs leading-5 text-gray-400">請輸入註冊時使用的電話號碼。</p>
              </div>
              <LoginForm @submit="submit" :disabled="posting" />
              <p
                v-if="!loginMethods.length"
                class="mt-3 hidden text-center text-xs text-primary desk:block"
              >
                <nuxt-link
                  :to="localePath('/auth/help')"
                  class="desk:hover:underline"
                >無法登錄？查看解決方法</nuxt-link>
              </p>
            </section>
          </div>
        </div>
      </div>
    </main>
  </AppView>
</template>

<script>
import FacebookIcon from "@/components/icons/FacebookIcon.vue";
import WechatIcon from "@/components/icons/WechatIcon.vue";
import GoogleIcon from "@/components/icons/GoogleIcon.vue";
import AppleIcon from "@/components/icons/AppleIcon.vue";
import MPayIcon from "@/components/icons/MPayIcon.vue";
import BocIcon from "@/components/icons/BocIcon.vue";
import AlipayHKIcon from "@/components/icons/AlipayHKIcon.vue";
import LoginForm from "@/components/forms/LoginForm.vue";
import {
  ClipboardListIcon,
  IdentificationIcon,
  LinkIcon,
} from "@vue-hero-icons/outline";
import {
  getAlipayHKAuthCode,
  getAlipayHKBridgeErrorMessage,
} from "@/utils/alipayhkMiniProgramBridge";

const ALIPAYHK_PHONE_SCOPES = ["SCOPE_USER_INFO_CONTACT"];
const ALIPAYHK_AUTH_BASE_SCOPES = ["auth_base"];

export default {
  components: {
    FacebookIcon,
    WechatIcon,
    GoogleIcon,
    AppleIcon,
    MPayIcon,
    LoginForm,
    BocIcon,
    AlipayHKIcon,
    ClipboardListIcon,
    IdentificationIcon,
    LinkIcon,
  },
  auth: "guest",
  head() {
    return {
      title: `${
        this.$store.getters["app/info"]?.name || process.env.APP_NAME
      }-會員登入`,
    };
  },
  data() {
    return {
      posting: false, // 提交中
    };
  },

  methods: {
    wechat() {
      this.login("wechat");
    },
    facebook() {
      this.login("facebook");
    },
    google() {
      this.login("google");
    },
    apple() {
      this.login("apple");
    },
    submit(data) {
      this.login("local", data);
    },
    mpay() {
      if (!my) return false;

      my.postMessage({
        type: "login",
      });

      my.onMessage = (e) => {
        this.login("local", {
          code: e.authCode,
          auth_type: "mpay",
        });
      };
    },
    bocmo() {
      this.login("bocmo");
    },
    async alipayhk() {
      if (typeof my === "undefined") return false;
      if (this.posting) return false;

      try {
        const result = await getAlipayHKAuthCode(ALIPAYHK_PHONE_SCOPES);
        this.loginWithAlipayHKAuthCode(result, ALIPAYHK_PHONE_SCOPES);
      } catch (e) {
        await this.alipayhkAuthBase();
      }
    },
    async alipayhkAuthBase() {
      try {
        const result = await getAlipayHKAuthCode(ALIPAYHK_AUTH_BASE_SCOPES);
        this.loginWithAlipayHKAuthCode(result, ALIPAYHK_AUTH_BASE_SCOPES);
      } catch (e) {
        this.$loader.error(
          "AlipayHK 授權失敗：" + getAlipayHKBridgeErrorMessage(e),
        );
      }
    },
    loginWithAlipayHKAuthCode(result, scopes) {
      this.login("local", {
        code: result.authCode,
        auth_type: "alipayhk",
        scopes,
        auth_success_scopes: result.authSuccessScopes || scopes,
        auth_error_scopes: result.authErrorScopes || [],
      });
    },
    isOauthStrategy(strategie) {
      return ["google", "apple", "facebook", "wechat", "bocmo"].includes(
        strategie,
      );
    },
    resetPosting() {
      this.posting = false;
    },
    onPageShow(e) {
      // bfcache / back from OAuth cancel must unlock UI
      if (e && e.persisted) {
        this.resetPosting();
      }
    },
    onVisibilityChange() {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        this.resetPosting();
      }
    },
    login(strategie, data) {
      // 當在提交中，不再提交
      if (this.posting) return;

      const isOauth = this.isOauthStrategy(strategie);
      // Full-page OAuth redirect: do not lock UI permanently if user cancels
      if (!isOauth) {
        this.posting = true;
      }

      this.$auth
        .loginWith(strategie, { data: data })
        .then(() => {
          if (this.$track) {
            this.$track.event("login", {
              method: strategie,
              auth_type: data?.auth_type,
              country_code: data?.country_code,
            });
          }
        })
        .catch((err) => {
          this.posting = false;
          if (this.errors && this.errors.user) {
            this.$loader.error(this.errors.user[0]);
          }
        })
        .finally(() => {
          // Local login stays on page; always clear posting on settle
          if (!isOauth) {
            this.posting = false;
          }
        });
    },
  },

  computed: {
    title() {
      return this.mode === "register" ? "會員註冊" : "會員登入";
    },
    loginMethods() {
      const methods = [
        {
          name: "wechat",
          icon: "wechat-icon",
          text: "Wechat 登入",
          show: () => this.inWeiXin,
          onClick: () => this.wechat(),
          class: "bg-[#2dc100]",
        },
        {
          name: "facebook",
          icon: "facebook-icon",
          text: "Facebook 登入",
          show: () => false,
          onClick: () => this.facebook(),
          class: "bg-[#4267b2]",
        },
        {
          name: "google",
          icon: "google-icon",
          text: "Google 登入",
          show: () => !this.inWeiXin && !this.inMpayMiniApp && !this.isAlipayHK,
          onClick: () => this.google(),
          class: "bg-[#ea4335]",
        },
        {
          name: "apple",
          icon: "apple-icon",
          text: "Apple 登入",
          show: () => !this.inWeiXin && !this.inMpayMiniApp && !this.isAlipayHK,
          onClick: () => this.apple(),
          class: "bg-black",
        },
        {
          name: "mpay",
          icon: "m-pay-icon",
          text: "MPay 登入",
          show: () => this.inMpayMiniApp,
          onClick: () => this.mpay(),
          class: "bg-[#FD8204]",
        },
        {
          name: "alipayhk",
          icon: "alipay-h-k-icon",
          text: "AlipayHK 一鍵登入",
          show: () => this.isAlipayHK,
          onClick: () => this.alipayhk(),
          class: "bg-[#1677FF]",
        },
        {
          name: "boc",
          icon: "boc-icon",
          text: "中銀 登入",
          show: () => false,
          onClick: () => this.bocmo(),
          class: "from-[#D80C2A] to-[#A51917] bg-gradient-to-b",
        },
      ];
      return methods.filter((method) => method.show());
    },
  },

  mounted() {
    this.resetPosting();
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", this.onPageShow);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }

    const { code, msg, authCode, auth_type, access_token } = this.$route.query;

    // AlipayHK OAuth 回調 — 小程式透過 URL redirect 傳回 authCode
    if (authCode && auth_type === "alipayhk") {
      this.login("local", {
        code: authCode,
        auth_type: "alipayhk",
        scopes: ALIPAYHK_AUTH_BASE_SCOPES,
        auth_success_scopes: ALIPAYHK_AUTH_BASE_SCOPES,
      });
      return;
    }

    // OAuth error callback: show msg and stay on login (no access_token)
    if (msg && !access_token) {
      this.$loader.error(code ? `CODE ${code}\r\n${msg}` : msg);
      return;
    }

    if (msg) {
      this.$loader.error(`CODE ${code}\r\n${msg}`);
    }
  },
  activated() {
    this.resetPosting();
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("pageshow", this.onPageShow);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }
  },
};
</script>

<style lang="css" scoped></style>
